/**
 * What the platform learns from the notes people write when they correct (contract v10.35,
 * ADR 0030 of the back): the criteria, their reach, and the one call that changes them.
 *
 * In `shared/` because two features need it: the email panel says what a note taught and
 * offers «Deshacer» (`PATCH` with `status: desactivado`), and Administración → «Lo que ha
 * aprendido» lists and edits every criterion. A feature never imports another one.
 */
import type { Schema } from '@/shared/api/contract';
import { api } from '@/shared/api/client';

export type GuidelineScope = Schema<'GuidelineScope'>;
export type GuidelineStatus = Schema<'GuidelineStatus'>;
export type Guideline = Schema<'GuidelineRead'>;
export type GuidelinePage = Schema<'GuidelinePage'>;
export type GuidelineUpdate = Schema<'GuidelineUpdate'>;
export type NoteLearning = Schema<'NoteLearningRead'>;
export type AppliedGuideline = Schema<'AppliedGuidelineRead'>;

/**
 * The reach of a criterion as a short name, for a badge and a select. `Record` on purpose:
 * a new scope of the back stops this file compiling instead of printing a slug.
 */
export const SCOPE_LABELS: Record<GuidelineScope, string> = {
  este_correo: 'Solo ese correo',
  remitente: 'Todo el remitente',
  remitente_si: 'El remitente, con condición',
  general: 'Cualquier remitente',
};

/** The reach as the end of «Aprendido: …», in the panel of the email. */
export const SCOPE_REACH: Record<GuidelineScope, string> = {
  este_correo: 'solo para este correo',
  remitente: 'para todos los correos de este remitente',
  remitente_si: 'para los correos de este remitente cuando se cumpla la condición',
  general: 'para cualquier remitente',
};

export const SCOPES: readonly GuidelineScope[] = Object.keys(SCOPE_LABELS) as GuidelineScope[];

/** Whether the reach takes a condition: the model judges if the email meets it. */
export function scopeHasCondition(scope: GuidelineScope): boolean {
  return scope === 'remitente_si' || scope === 'general';
}

/** The tabs of «Lo que ha aprendido», in the plural the counts read with. */
export const STATUS_LABELS: Record<GuidelineStatus, string> = {
  vigente: 'Vigentes',
  sustituido: 'Sustituidos',
  desactivado: 'Desactivados',
};

export const STATUSES: readonly GuidelineStatus[] = Object.keys(STATUS_LABELS) as GuidelineStatus[];

export const guidelinesRootKey = ['guidelines'] as const;

export interface GuidelineFilters {
  status_filter?: GuidelineStatus | undefined;
  /** Words that must all appear in the sender, the criterion, the condition or the note
   * (contract v10.36), without accents: how a superadministrator finds what names someone. */
  q?: string | undefined;
  offset?: number | undefined;
}

/** One page of «Lo que ha aprendido»: 100 criteria, the API's default. */
export const GUIDELINES_PAGE = 100;

export const guidelineKeys = {
  all: guidelinesRootKey,
  list: (filters: GuidelineFilters) => [...guidelinesRootKey, 'list', filters] as const,
};

export const guidelinesApi = {
  /** Whoever administers; a superadministrator sees all, the rest their mailboxes' profiles. */
  list: (filters: GuidelineFilters): Promise<GuidelinePage> =>
    api.get<GuidelinePage>('/api/admin/guidelines', {
      status_filter: filters.status_filter,
      q: filters.q || undefined,
      limit: GUIDELINES_PAGE,
      offset: filters.offset ?? 0,
    }),

  /**
   * Only the fields sent change. The author of the note may send `status` alone (the
   * «Deshacer» of the panel); everything else is for whoever administers. 422 with a
   * Spanish `detail` on a combination that cannot be, 404 if the person cannot see it.
   */
  update: (id: string, change: GuidelineUpdate): Promise<Guideline> =>
    api.patch<Guideline>(`/api/admin/guidelines/${id}`, change),

  /**
   * Delete one for good (contract v10.36): a superadministrator, elevated, for a request to
   * erase someone's data. Its text leaves the «por qué» of every email it bore on; the note
   * stays on its correction and is not learned again.
   */
  remove: (id: string): Promise<void> => api.delete<void>(`/api/admin/guidelines/${id}`),
};
